import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The URL is the one place a list page keeps its filters, search, sort, page
 * and open drawer, so that:
 *   - Back from a detail page returns to exactly the same list;
 *   - a refresh keeps what the admin was looking at;
 *   - a URL can be pasted to a colleague.
 *
 * Filter edits REPLACE the current history entry (typing a search must not
 * create ten Back steps). Opening a drawer PUSHES one, so the browser Back
 * button closes the drawer instead of leaving the page.
 */
export type UrlPatch = Record<string, string | number | boolean | null | undefined>;

export function useUrlState() {
  const [params, setParams] = useSearchParams();

  const set = useCallback(
    (patch: UrlPatch, opts: { push?: boolean } = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === undefined || v === '' || v === false) next.delete(k);
            else next.set(k, v === true ? '1' : String(v));
          }
          return next;
        },
        { replace: !opts.push },
      );
    },
    [setParams],
  );

  const get = useCallback((key: string): string | null => params.get(key), [params]);

  return { params, get, set };
}

/** `value` if it is one of `allowed`, else `fallback`. */
export function oneOf<T extends string>(
  value: string | null | undefined,
  allowed: readonly T[],
  fallback: T | undefined = undefined,
): T | undefined {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/** A positive integer param, else `fallback`. */
export function intParam(value: string | null | undefined, fallback: number): number {
  if (value == null || !/^\d+$/.test(value)) return fallback;
  const n = Number(value);
  return n > 0 ? n : fallback;
}

/** A `YYYY-MM-DD` param, or '' when absent / malformed. */
export function dayParam(value: string | null | undefined): string {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

/** One of the allowed page sizes, else the first. */
export function pageSizeParam(value: string | null | undefined, allowed: readonly number[]): number {
  const first = allowed[0] ?? 25;
  const n = intParam(value, first);
  return allowed.includes(n) ? n : first;
}
